import { lazy, Suspense } from 'react'
import { Link } from 'react-router'
import { Clock3, Crown, Languages, Target } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GlobeArt } from '@/components/brand/GlobeArt'
import { AdSlot, Button, Skeleton } from '@/components/ui/primitives'
import { ConverterCard } from '@/features/convert/ConverterCard'
import { QuickConversions } from '@/features/convert/QuickConversions'
import { MarketStrip, MoversCard } from '@/features/markets/MarketWidgets'
import { usePageTitle } from '@/lib/usePageTitle'

// Chart library is code-split so the converter renders first.
const PriceChartCard = lazy(() =>
  import('@/features/markets/PriceChartCard').then((m) => ({ default: m.PriceChartCard })),
)

function Hero() {
  const { t } = useTranslation()
  const points = [
    { icon: Clock3, text: t('home.point1') },
    { icon: Target, text: t('home.point2') },
    { icon: Languages, text: t('home.point3') },
  ]
  return (
    <section className="relative overflow-hidden rounded-[1.75rem] border border-white/10 text-white shadow-pop [background:var(--hero-grad)]">
      <GlobeArt className="absolute -end-24 -bottom-40 size-[26rem] opacity-70 sm:-end-16 sm:size-[30rem] lg:-end-10 lg:-bottom-56 lg:size-[40rem] lg:opacity-90" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgb(255_255_255/0.07)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />

      <div className="relative grid grid-cols-1 gap-5 p-4 sm:p-6 lg:grid-cols-12 lg:items-center lg:gap-8 lg:p-10">
        <div className="lg:col-span-6 xl:col-span-7">
          <p className="mb-2 text-xs font-semibold tracking-[0.18em] text-gold-300 uppercase lg:mb-4">GlobalX</p>
          <h1 className="text-[1.6rem] leading-tight font-bold tracking-tight sm:text-4xl lg:text-[2.9rem] lg:leading-[1.15]">
            {t('home.heroTitle')}
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base lg:mt-4">
            {t('home.heroSubtitle')}
          </p>
          <ul className="mt-6 hidden flex-col gap-3 lg:flex">
            {points.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-sm text-white/80">
                <span className="flex size-9 items-center justify-center rounded-xl bg-white/8 ring-1 ring-white/12">
                  <Icon className="size-4 text-gold-300" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 xl:col-span-5">
          <ConverterCard className="text-fg shadow-pop" />
        </div>
      </div>
    </section>
  )
}

function ProPromo() {
  const { t } = useTranslation()
  return (
    <Link
      to="/pro"
      className="card flex items-center gap-4 border-gold-400/25! bg-gradient-to-br from-gold-400/10 via-transparent to-blue-500/10 p-4 lg:hidden"
    >
      <span className="flex size-11 items-center justify-center rounded-2xl gold-fill shadow-gold">
        <Crown className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">{t('home.proTitle')}</span>
        <span className="block text-xs text-muted">{t('home.proText')}</span>
      </span>
      <Button variant="outline" size="sm" tabIndex={-1}>
        {t('home.proCta')}
      </Button>
    </Link>
  )
}

export function HomePage() {
  usePageTitle()
  return (
    <div className="flex flex-col gap-6 lg:gap-8">
      <Hero />
      <QuickConversions />
      <MarketStrip />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
        <Suspense fallback={<Skeleton className="h-[460px] rounded-[1.25rem] lg:col-span-8" />}>
          <PriceChartCard className="lg:col-span-8" />
        </Suspense>
        <MoversCard className="lg:col-span-4" />
      </div>
      <ProPromo />
      <AdSlot />
    </div>
  )
}
